/**
 * ErrorBoundary Component
 * Catches JavaScript errors anywhere in the child component tree
 * and displays a fallback UI instead of crashing the entire app
 */

import React, { Component, type ReactNode, useMemo, useContext } from 'react';
import { View, Text, Pressable, Platform, ScrollView } from 'react-native';
import { WEB_HOVER_TRANSITION_STYLE } from '../../src/shared/constants/animations';
import { useRouter } from 'expo-router';
import { getTheme } from '../../src/theme/getTheme';
import { theme as defaultTheme } from '../../theme';
import { ThemeContext } from '../../src/theme/ThemeContext';
import { getErrorBoundaryStyles } from './ErrorBoundary.styles';
import { reportClientError } from '../../src/shared/helpers/reportClientError.helper';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * ErrorBoundary class component
 * Must be a class component to catch errors (React requirement)
 */
class ErrorBoundaryClass extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to console for debugging (allowed in ErrorBoundary)
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    reportClientError(error, {
      componentStack: errorInfo.componentStack ?? undefined,
    });

    // Call optional onError callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorFallback component - displays error message and reset button
 */
interface ErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
}

const ErrorFallback: React.FC<ErrorFallbackProps> = ({ error, onReset }) => {
  const router = useRouter();
  
  // Safely get theme - use ThemeContext if available, otherwise fallback to default theme
  // ErrorBoundary might render before ThemeProvider is mounted, so we need safe access
  // Must call useContext unconditionally (React Hooks rule)
  const themeContext = useContext(ThemeContext);
  
  const theme = useMemo(() => {
    // Check if context is available and has themeMode
    if (themeContext?.themeMode) {
      return getTheme(themeContext.themeMode);
    }
    // Fallback to default theme if context is not available
    return defaultTheme;
  }, [themeContext]);

  const styles = useMemo(() => getErrorBoundaryStyles(theme), [theme]);

  const handleGoHome = () => {
    onReset();
    router.replace('/');
  };

  return (
    <View style={styles.container} testID="error-boundary-fallback">
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.content}>
          <Text style={styles.title}>Something went wrong</Text>
          <Text style={styles.message}>
            We're sorry, but something unexpected happened. Please try again.
          </Text>
          {error && __DEV__ && (
            <View style={styles.errorDetails} testID="error-boundary-details">
              <Text style={styles.errorTitle}>Error Details (Dev Only):</Text>
              <Text style={styles.errorText}>{error.message}</Text>
              {error.stack && (
                <Text style={styles.errorStack}>{error.stack}</Text>
              )}
            </View>
          )}
          <View style={styles.actions}>
            <Pressable
              style={({ pressed, hovered }) => [
                styles.button,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.buttonPressed,
                hovered && Platform.OS === 'web' && styles.buttonHover,
              ]}
              onPress={onReset}
              accessibilityRole="button"
              accessibilityLabel="Try again"
              testID="error-boundary-try-again-button"
            >
              <Text style={styles.buttonText}>Try Again</Text>
            </Pressable>
            <Pressable
              style={({ pressed, hovered }) => [
                styles.button,
                styles.buttonSecondary,
                Platform.select({ web: WEB_HOVER_TRANSITION_STYLE }),
                pressed && styles.buttonPressed,
                hovered && Platform.OS === 'web' && styles.buttonHover,
              ]}
              onPress={handleGoHome}
              accessibilityRole="button"
              accessibilityLabel="Go to home"
              testID="error-boundary-go-home-button"
            >
              <Text style={[styles.buttonText, styles.buttonTextSecondary]}>
                Go Home
              </Text>
            </Pressable>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

/**
 * ErrorBoundary functional wrapper
 * Exports a functional component that wraps the class component
 */
export const ErrorBoundary: React.FC<ErrorBoundaryProps> = (props) => {
  return <ErrorBoundaryClass {...props} />;
};
